import {Box, HStack, Heading, Image, VStack} from '@chakra-ui/react';
import {ChevronLeftIcon} from '@chakra-ui/icons';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {capitalizeFirstLetter, routes} from 'ff-common';
import {
  CategorieMarketplace,
  HitItemCatalogue,
  Marketplace,
} from 'api-types/ffconnect';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {useMarketplaceBrandColors} from '@/hooks/marketplace/useMarketplaceBrandColors';
import {HookUseTypedFilters} from '@synako/typed-path';
import {FiltresItemCatalogue} from 'ff-common/src/hooks/search/use-search';
import {Results} from '@orama/orama';
import {State} from '@synako/halitrek';
import {MarketplaceDesc} from 'api-types/ffconnect-hal';
import {HaliRenderOne} from '@synako/haligator';
import {CatalogMarketplaceBreadcrumb} from './CatalogMarketplaceBreadcrumb';
import {MarketplaceCategoryFiltersBar} from './MarketplaceCategoryFiltersBar';

function buildCategoryAncestryPath(
  currentCategoryId: string,
  items: CategorieMarketplace[],
): CategorieMarketplace[] {
  const current = items.find((cat) => cat.id === currentCategoryId);
  if (!current) return [];
  const path: CategorieMarketplace[] = [current];
  let cat: CategorieMarketplace | undefined = current;
  while (cat.id_parent) {
    const parent = items.find((c) => c.id === cat!.id_parent);
    if (!parent) break;
    path.push(parent);
    cat = parent;
  }
  return path.reverse();
}

interface CatalogMarketplaceHeaderProps {
  marketplace: Marketplace;
  filtersHook?: HookUseTypedFilters<FiltresItemCatalogue>;
  facets?: Results<HitItemCatalogue>['facets'];
  marketplaceState?: State<MarketplaceDesc>;
}

export const CatalogMarketplaceHeader: React.FC<
  CatalogMarketplaceHeaderProps
> = ({marketplace, filtersHook, facets, marketplaceState}) => {
  const navigate = useNavigate();
  const {t} = useTranslation();
  const {buildClientRoute} = useClientRoute();
  const {couleur, textCouleur} = useMarketplaceBrandColors(
    marketplace.marketplace_id,
  );
  const bgColor = couleur ?? 'categories.frais';
  const textColor = textCouleur ?? 'gray.dark';
  const headingTextColor = textCouleur ?? 'black.text';

  const baseCrumbs: {name: string; link?: string; onClick?: () => void}[] = [
    {name: t('global.accueil'), link: buildClientRoute(routes.index)},
    {name: marketplace.nom},
  ];

  const canBuildDynamicCrumbs = Boolean(filtersHook && marketplaceState);

  return (
    <Box
      position='relative'
      overflow='hidden'
      borderRadius='12px'
      mx={{base: '2', md: '4', lg: '6'}}
      p={{base: '0px', md: '20px'}}
      bg={bgColor}
    >
      <HStack
        pt={2}
        justify='space-between'
        position='relative'
        zIndex={1}
      >
        <VStack
          align='left'
          justifyContent='space-between'
          minHeight='126px'
          flex='1'
          minW={0}
          paddingLeft='20px'
          display={{base: 'none', md: 'flex'}}
        >
          {canBuildDynamicCrumbs ? (
            <HaliRenderOne
              nav={marketplaceState!.follow('nomenclature')}
              render={({data: dataNomenclature}) => {
                const currentCategoryId =
                  filtersHook!.params.cat_marketplace?.[0];
                const items = dataNomenclature.items;
                const categoryPath = currentCategoryId
                  ? buildCategoryAncestryPath(currentCategoryId, items)
                  : [];
                const crumbs: {
                  name: string;
                  link?: string;
                  onClick?: () => void;
                }[] = [
                  {
                    name: t('global.accueil'),
                    link: buildClientRoute(routes.index),
                  },
                  ...(categoryPath.length > 0
                    ? [
                        {
                          name: marketplace.nom,
                          onClick: () => {
                            filtersHook!.change('cat_marketplace', undefined);
                          },
                        },
                        ...categoryPath.slice(0, -1).map((cat) => ({
                          name: capitalizeFirstLetter(cat.libelle),
                          onClick: () => {
                            filtersHook!.change('cat_marketplace', cat.id);
                          },
                        })),
                        {
                          name: capitalizeFirstLetter(
                            categoryPath[categoryPath.length - 1].libelle,
                          ),
                        },
                      ]
                    : [{name: marketplace.nom}]),
                ];
                return (
                  <CatalogMarketplaceBreadcrumb
                    crumbs={crumbs}
                    textColor={textColor}
                    headingTextColor={headingTextColor}
                  />
                );
              }}
            />
          ) : (
            <CatalogMarketplaceBreadcrumb
              crumbs={baseCrumbs}
              textColor={textColor}
              headingTextColor={headingTextColor}
            />
          )}
          {filtersHook && facets !== undefined && marketplaceState && (
            <Box
              width='100%'
              flex='1'
            >
              <MarketplaceCategoryFiltersBar
                filtersHook={filtersHook}
                facets={facets}
                marketplaceState={marketplaceState}
              />
            </Box>
          )}
        </VStack>
        <Box
          position='relative'
          display={{base: 'none', md: 'flex'}}
          mr={4}
          justifyContent='flex-end'
        >
          {marketplace.logo_vertical && (
            <Image
              src={`${import.meta.env['VITE_MEDIA_URL']}${marketplace.logo_vertical}`}
              alt={marketplace.nom}
              maxWidth='100%'
              maxHeight='100%'
              objectFit='contain'
            />
          )}
        </Box>
      </HStack>
      <Box
        display={{base: 'flex', md: 'none'}}
        bg={bgColor}
        p={4}
      >
        <HStack
          onClick={() => {
            navigate(-1);
          }}
          cursor='pointer'
        >
          <ChevronLeftIcon
            boxSize='25px'
            color={headingTextColor}
          />
          <Heading
            as='h5'
            fontSize='lg'
            fontWeight='700'
            color={headingTextColor}
          >
            {marketplace.nom}
          </Heading>
        </HStack>
      </Box>
    </Box>
  );
};
